import * as React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Seats } from '@ixicare/resources';
import { t } from '~/i18n';

interface SiteMaxCellProps {
  siteUuid: string;
  value: number | null;
  /** The month's peak, i.e. what this period actually bills at. */
  alarmReceivers: number;
  editable: boolean;
}

// A maximum below the month's peak is not an error — the period simply bills on the
// peak instead — but it costs money, so it should stand out at a glance. Only that
// case is coloured: a maximum that covers usage is the normal state and needs no
// marking, and nothing is coloured when no maximum is set, since unlimited is neither
// over nor under.
export const maxColour = (value: number | null, alarmReceivers: number): string | undefined =>
  value !== null && value < alarmReceivers ? 'var(--color-danger)' : undefined;

// Editable maximum for one site. Blank clears it, which removes the limit.
//
// A maximum below current usage is accepted on purpose: nobody is logged out for it,
// the number drains as people leave, and the period bills on the higher of used and
// max. Refusing it would make downgrading impossible.
export const SiteMaxCell: React.FC<SiteMaxCellProps> = ({ siteUuid, value, alarmReceivers, editable }) => {
  const queryClient = useQueryClient();
  const [draft, setDraft] = React.useState<string>(value === null ? '' : String(value));

  // Follow the server once it answers, unless the field is being edited right now.
  const [isFocused, setIsFocused] = React.useState(false);
  React.useEffect(() => {
    if (!isFocused) setDraft(value === null ? '' : String(value));
  }, [value, isFocused]);

  const mutation = useMutation({
    mutationFn: (next: number | null) => Seats.setSiteMaxConcurrent(siteUuid, next),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['billingBreakdown'] }),
    onError: () => setDraft(value === null ? '' : String(value)),
  });

  if (!editable) {
    return value === null ? (
      <>{t('billing.seats.noLimit')}</>
    ) : (
      <span style={{ color: maxColour(value, alarmReceivers) }}>{value}</span>
    );
  }

  const commit = () => {
    const trimmed = draft.trim();
    const next = trimmed === '' ? null : Number(trimmed);
    if (next !== null && (!Number.isInteger(next) || next < 0)) {
      setDraft(value === null ? '' : String(value));
      return;
    }
    if (next === value) return;
    mutation.mutate(next);
  };

  return (
    <input
      type="number"
      min={0}
      step={1}
      value={draft}
      disabled={mutation.isPending}
      title={t('billing.seats.maxHint')}
      placeholder={t('billing.seats.noLimit')}
      onChange={(event) => setDraft(event.target.value)}
      onFocus={() => setIsFocused(true)}
      onBlur={() => {
        setIsFocused(false);
        commit();
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') event.currentTarget.blur();
        if (event.key === 'Escape') {
          setDraft(value === null ? '' : String(value));
          event.currentTarget.blur();
        }
      }}
      style={{ width: 72, padding: '2px 6px', color: maxColour(value, alarmReceivers) }}
    />
  );
};
